import { z } from 'zod';
import {
  type RevisionColorKey,
  revisionColorRank,
  revisionColorSchema,
  versionFormatSchema,
} from './colors';

/**
 * How a version marks its revisions: `color`, the paper colors (the 44 keys); `custom`, the provider's own marks
 * ("Rev 4", "v2.3", a draft date), with the unmarked pages of a collated copy as one row with no label; `changed`,
 * no per-page marks at all, one row of changed pages with no label.
 */
export const revisionSchemeSchema = z.enum(['color', 'custom', 'changed']);
export type RevisionScheme = z.infer<typeof revisionSchemeSchema>;

/** A custom label as the page prints it: trimmed, 1 to 40 characters. It is script text (never logged). */
export const revisionLabelSchema = z.string().trim().min(1).max(40);

/** A revision row's label: a color key, a custom label, or null for the unmarked set or a `changed` row. */
export type RevisionLabel = string | null;

function custom(label: string): string {
  return label.trim().toLowerCase();
}

/**
 * The labels of a version that are new, in the order given: under `color`, a key no earlier `color` version has;
 * under `custom`, a label no earlier `custom` version has (trimmed, case-insensitive), the null label new only when
 * there is no earlier version at all; under `changed`, every label. `sandbox_push_revisions` holds the same rule in
 * SQL, so the CLI's check and the database agree.
 */
export function newLabelsFor(
  scheme: RevisionScheme,
  labels: Iterable<RevisionLabel>,
  earlier: { scheme: RevisionScheme; labels: Iterable<RevisionLabel> }[],
): RevisionLabel[] {
  const own = [...new Set(labels)];
  if (scheme === 'changed') return own;
  const seen = new Set<string>();
  for (const v of earlier) {
    if (v.scheme !== scheme) continue;
    for (const l of v.labels) if (l !== null) seen.add(scheme === 'custom' ? custom(l) : l);
  }
  return own.filter((l) =>
    l === null ? earlier.length === 0 : !seen.has(scheme === 'custom' ? custom(l) : l),
  );
}

/**
 * The rows in display order: color rank under `color`; lowest page first under `custom`, the unmarked set last;
 * as given under `changed`. The view `script_version_scopes` orders its arrays the same way.
 */
export function sortLabels<T extends { label: RevisionLabel; pages: number[] }>(
  scheme: RevisionScheme,
  rows: readonly T[],
): T[] {
  const list = [...rows];
  if (scheme === 'color')
    return list.sort(
      (a, b) =>
        revisionColorRank(a.label as RevisionColorKey) -
        revisionColorRank(b.label as RevisionColorKey),
    );
  if (scheme === 'custom') {
    const first = (r: T) => (r.label === null ? Number.POSITIVE_INFINITY : Math.min(...r.pages));
    return list.sort((a, b) => first(a) - first(b));
  }
  return list;
}

const labelsSchema = z.array(z.string().nullable());

function labelsFit(scheme: RevisionScheme, labels: RevisionLabel[]): boolean {
  if (scheme === 'color') return labels.every((l) => revisionColorSchema.safeParse(l).success);
  if (scheme === 'changed') return labels.every((l) => l === null);
  return true;
}

/**
 * The scope: the version's scheme and format, its labels and the new ones (a null element for the unmarked set or
 * a `changed` row) in `sortLabels` order, and the pages a run analyses. `scope.json` in the sandbox is this shape (`revisionScopeSchema`).
 */
export const revisionScopeSchema = z
  .object({
    scheme: revisionSchemeSchema,
    format: versionFormatSchema,
    labels: labelsSchema,
    new_labels: labelsSchema,
    analyzed_pages: z.array(z.number().int().positive()),
  })
  .refine((s) => labelsFit(s.scheme, s.labels) && labelsFit(s.scheme, s.new_labels), {
    message: 'the labels do not fit the scheme',
    path: ['labels'],
  });
export type RevisionScope = z.infer<typeof revisionScopeSchema>;

/** A row of the `script_version_scopes` view: the scope with its version and project ids. */
export const scriptVersionScopeRowSchema = revisionScopeSchema.safeExtend({
  script_version_id: z.uuid(),
  project_id: z.uuid(),
});
export type ScriptVersionScopeRow = z.infer<typeof scriptVersionScopeRowSchema>;

/** The labels a version's pills show: all of version 1's, a later version's new ones. */
export function pillLabelsFor(
  row: { labels: RevisionLabel[]; new_labels: RevisionLabel[] },
  versionNumber: number,
): RevisionLabel[] {
  return versionNumber === 1 ? row.labels : row.new_labels;
}
